import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Responsive, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  DollarSign, ShoppingCart, Package, Users, AlertTriangle,
  TrendingUp, ArrowDown, Building2, Wallet, Receipt, ArrowRight,
  Truck, Clock, CreditCard, Banknote, CheckCircle2, Calendar,
  BarChart3, RefreshCw, ArrowUpRight, ArrowDownRight, Lock, Unlock, ShieldCheck,
  CalendarClock, ChevronDown, ChevronRight, FileCheck, GripVertical,
  RotateCcw, Smartphone, WifiOff, CloudOff, Percent
} from 'lucide-react';
import { localTodayStr } from '../lib/dateFormat';
import { formatPHP } from '../lib/utils';
import { useOnlineStatus } from '../lib/useOnlineStatus';
import ReviewDetailDialog from '../components/ReviewDetailDialog';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import CustomerStatementModal from '../components/CustomerStatementModal';
import PendingReviewsWidget from '../components/PendingReviewsWidget';
import SalesTrendsWidget from '../components/dashboard/SalesTrendsWidget';
import BranchComparisonWidget from '../components/dashboard/BranchComparisonWidget';
import AccountsPayableWidget from '../components/dashboard/AccountsPayableWidget';
import InternalProfitWidget from '../components/dashboard/InternalProfitWidget';
import AuditPulseWidget from '../components/dashboard/AuditPulseWidget';
import QuickLaunch from '../components/QuickLaunch';

const LAYOUT_KEY = 'agribooks_dashboard_layout';

// Minimum heights per widget to prevent collapsing
const MIN_H_MAP = {
  kpis: 5, 'sales-trends': 5, ap: 5, 'branch-comparison': 5,
  'pending-reviews': 4, branches: 7, payables: 4, alerts: 4,
  'top-revenue': 4, 'internal-profit': 6, 'cash-position': 5,
  'ar-overview': 5, 'credits-today': 5, 'ar-payments': 5,
  'top-products': 4, 'recent-sales': 4, 'audit-pulse': 7,
};
const MIN_W_MAP = {
  kpis: 6, 'sales-trends': 4, ap: 3, 'branch-comparison': 3,
  'pending-reviews': 4, branches: 6, payables: 3, alerts: 3,
  'top-revenue': 3, 'internal-profit': 4, 'cash-position': 3,
  'ar-overview': 4, 'credits-today': 3, 'ar-payments': 3,
  'top-products': 3, 'recent-sales': 3, 'audit-pulse': 3,
};

// Validates a saved layout — resets to defaults if any widget is smaller than minH
function validateLayouts(saved, defaults) {
  try {
    const isValid = Object.keys(defaults).every(bp => {
      if (!saved[bp] || !Array.isArray(saved[bp])) return false;
      return defaults[bp].every(item => {
        const s = saved[bp].find(x => x.i === item.i);
        if (!s) return false;
        return s.h >= (MIN_H_MAP[item.i] ?? 2) && s.w >= (MIN_W_MAP[item.i] ?? 2);
      });
    });
    return isValid ? saved : defaults;
  } catch { return defaults; }
}

// ── Shared small components ──────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon, color, testId }) {
  return (
    <div
      className="rounded-xl bg-white p-5 h-full min-w-0 flex flex-col justify-between transition-all duration-200 hover:-translate-y-0.5"
      style={{ border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-card)' }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className="text-[11px] font-bold uppercase tracking-[0.12em] leading-tight"
          style={{ color: 'var(--text-secondary)' }}
        >
          {label}
        </span>
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${color.bg}`}>
          <Icon size={15} className={color.text} strokeWidth={1.9} />
        </span>
      </div>
      <p
        className={`text-[26px] font-black leading-none tracking-tight mt-2 ${color.text}`}
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontFeatureSettings: '"tnum"',
          fontFamily: 'Manrope, sans-serif',
        }}
      >
        {value}
      </p>
      <p
        className="text-[11px] font-medium mt-2 line-clamp-2 leading-snug min-h-[1rem]"
        style={{ color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}
      >
        {sub || '\u00A0'}
      </p>
    </div>
  );
}

function AgingBar({ aging }) {
  const total = aging?.total || 1;
  return (
    <div className="space-y-1.5 text-xs">
      {[
        { label: '0–30d', val: aging?.current || 0, color: 'bg-emerald-400' },
        { label: '31–60d', val: aging?.days_31_60 || 0, color: 'bg-amber-400' },
        { label: '61–90d', val: aging?.days_61_90 || 0, color: 'bg-orange-400' },
        { label: '90+d', val: aging?.over_90 || 0, color: 'bg-red-500' },
      ].map(({ label, val, color }) => (
        <div key={label}>
          <div className="flex justify-between mb-0.5 text-[10px]">
            <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
            <span className="font-semibold" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{formatPHP(val)}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--neutral-bg)' }}>
            <div className={`h-full ${color} rounded-full`} style={{ width: `${Math.max(2, (val / total) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function BranchCard({ branch, onSelect }) {
  const today = localTodayStr();
  const lastClose = branch.last_close_date;
  const daysSinceClose = lastClose ? Math.floor((new Date(today) - new Date(lastClose)) / 86400000) : null;
  const dot = branch.status === 'good' ? 'bg-emerald-400' : branch.status === 'warning' ? 'bg-amber-400' : 'bg-red-500';

  return (
    <Card className="border-slate-200 hover:border-[#1A4D2E]/40 transition-all cursor-pointer hover:shadow-md group" onClick={() => onSelect(branch.id)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><div className={`w-2 h-2 rounded-full ${dot}`} /><h3 className="font-semibold text-sm">{branch.name}</h3></div>
          <ArrowRight size={13} className="text-slate-300 group-hover:text-[#1A4D2E] transition-colors" />
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div><p className="text-slate-400">Total Sales</p><p className="font-bold text-emerald-700">{formatPHP(branch.today_revenue || 0)}</p></div>
          <div><p className="text-slate-400">Cash Sales</p><p className="font-bold text-green-700">{formatPHP(branch.today_cash_sales || 0)}</p></div>
          <div><p className="text-slate-400">New Credit</p><p className="font-bold text-amber-700">{formatPHP(branch.today_new_credit || 0)}</p></div>
          <div><p className="text-slate-400">Digital Sales</p><p className="font-bold text-indigo-700">{formatPHP(branch.today_digital_sales || 0)}</p></div>
          <div><p className="text-slate-400">Cashier + Safe</p><p className="font-bold text-blue-700">{formatPHP(branch.total_cash || 0)}</p></div>
          <div><p className="text-slate-400">AR Outstanding</p><p className={`font-bold ${branch.receivables > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{formatPHP(branch.receivables || 0)}</p></div>
        </div>
        {branch.inventory_value && (<><Separator className="my-2" /><div className="grid grid-cols-2 gap-x-4 text-[10px]">
          <div><span className="text-slate-400">Stock @ Capital</span><p className="font-semibold text-slate-700">{formatPHP(branch.inventory_value.capital_value)}</p></div>
          <div><span className="text-slate-400">Stock @ Retail</span><p className="font-semibold text-emerald-700">{formatPHP(branch.inventory_value.retail_value)}</p></div>
        </div></>)}
        <Separator className="my-2" />
        <div className="flex items-center justify-between text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><Lock size={9} /> {lastClose ? daysSinceClose === 0 ? 'Closed today' : `Last close: ${lastClose}` : 'Never closed'}</span>
          {branch.low_stock_count > 0 && <span className="flex items-center gap-1 text-amber-600"><AlertTriangle size={9} /> {branch.low_stock_count} low stock</span>}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Grid Widget Wrapper ──────────────────────────────────────────────────────
function GridWidget({ title, children, className = '', locked = true }) {
  return (
    <div
      className={`h-full flex flex-col bg-white rounded-xl overflow-hidden transition-shadow relative ${
        locked
          ? 'border border-[var(--border-default)]'
          : 'border-2 border-dashed border-stone-400'
      } ${className}`}
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      {title && (
        <div
          className={`flex items-center gap-2 px-3 py-2 border-b shrink-0 ${
            locked ? '' : 'cursor-grab active:cursor-grabbing drag-handle bg-stone-50'
          }`}
          style={{ borderColor: 'var(--border-subtle)' }}
        >
          {!locked && <GripVertical size={12} className="text-stone-400" />}
          <span
            className="text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: 'var(--text-tertiary)' }}
          >
            {title}
          </span>
        </div>
      )}
      {/* Titleless widgets (KPI band, branches) are static anchors — no drag
          handle is shown. Titled widgets expose the header handle when unlocked. */}
      <div className="flex-1 overflow-auto p-0 relative">{children}</div>
    </div>
  );
}

// ── DEFAULT LAYOUTS ──────────────────────────────────────────────────────────
// Arranged by business-scan priority (what an owner glances at first → last):
//   Row 1: KPI pulse band  ·  Row 2: Money (Sales trend + Payables/AR)
//   Row 3: Operations (Branches / lists)  ·  Row 4: Actionables (reviews/audit)
const OWNER_DEFAULT_LAYOUT = {
  lg: [
    { i: 'kpis', x: 0, y: 0, w: 12, h: 5, static: true, minH: 5, minW: 6 },
    { i: 'sales-trends', x: 0, y: 5, w: 8, h: 10, minH: 5, minW: 4 },
    { i: 'ap', x: 8, y: 5, w: 4, h: 10, minH: 5, minW: 3 },
    { i: 'branches', x: 0, y: 15, w: 8, h: 10, static: true, minH: 7, minW: 6 },
    { i: 'top-revenue', x: 8, y: 15, w: 4, h: 10, minH: 4, minW: 3 },
    { i: 'pending-reviews', x: 0, y: 25, w: 4, h: 9, minH: 4, minW: 4 },
    { i: 'payables', x: 4, y: 25, w: 4, h: 9, minH: 4, minW: 3 },
    { i: 'audit-pulse', x: 8, y: 25, w: 4, h: 9, minH: 7, minW: 3 },
    { i: 'branch-comparison', x: 0, y: 34, w: 6, h: 9, minH: 5, minW: 3 },
    { i: 'alerts', x: 6, y: 34, w: 6, h: 9, minH: 4, minW: 3 },
    { i: 'internal-profit', x: 0, y: 43, w: 12, h: 10, minH: 6, minW: 4 },
  ],
  md: [
    { i: 'kpis', x: 0, y: 0, w: 10, h: 6, static: true, minH: 5, minW: 6 },
    { i: 'sales-trends', x: 0, y: 6, w: 10, h: 9, minH: 5, minW: 4 },
    { i: 'ap', x: 0, y: 15, w: 5, h: 9, minH: 5, minW: 3 },
    { i: 'top-revenue', x: 5, y: 15, w: 5, h: 9, minH: 4, minW: 3 },
    { i: 'branches', x: 0, y: 24, w: 10, h: 10, static: true, minH: 7, minW: 6 },
    { i: 'pending-reviews', x: 0, y: 34, w: 5, h: 9, minH: 4, minW: 4 },
    { i: 'payables', x: 5, y: 34, w: 5, h: 9, minH: 4, minW: 3 },
    { i: 'audit-pulse', x: 0, y: 43, w: 5, h: 9, minH: 7, minW: 3 },
    { i: 'branch-comparison', x: 5, y: 43, w: 5, h: 9, minH: 5, minW: 3 },
    { i: 'alerts', x: 0, y: 52, w: 10, h: 7, minH: 4, minW: 3 },
    { i: 'internal-profit', x: 0, y: 59, w: 10, h: 10, minH: 6, minW: 4 },
  ],
};

const BRANCH_DEFAULT_LAYOUT = {
  lg: [
    { i: 'kpis', x: 0, y: 0, w: 12, h: 5, static: true, minH: 5, minW: 6 },
    { i: 'sales-trends', x: 0, y: 5, w: 8, h: 10, minH: 5, minW: 4 },
    { i: 'cash-position', x: 8, y: 5, w: 4, h: 10, minH: 5, minW: 3 },
    { i: 'ar-overview', x: 0, y: 15, w: 8, h: 8, minH: 5, minW: 4 },
    { i: 'ap', x: 8, y: 15, w: 4, h: 8, minH: 5, minW: 3 },
    { i: 'recent-sales', x: 0, y: 23, w: 6, h: 9, minH: 4, minW: 3 },
    { i: 'top-products', x: 6, y: 23, w: 6, h: 9, minH: 4, minW: 3 },
    { i: 'credits-today', x: 0, y: 32, w: 6, h: 7, minH: 5, minW: 3 },
    { i: 'ar-payments', x: 6, y: 32, w: 6, h: 7, minH: 5, minW: 3 },
    { i: 'pending-reviews', x: 0, y: 39, w: 4, h: 9, minH: 4, minW: 4 },
    { i: 'payables', x: 4, y: 39, w: 4, h: 9, minH: 4, minW: 3 },
    { i: 'audit-pulse', x: 8, y: 39, w: 4, h: 9, minH: 7, minW: 3 },
  ],
  md: [
    { i: 'kpis', x: 0, y: 0, w: 10, h: 7, static: true, minH: 5, minW: 6 },
    { i: 'sales-trends', x: 0, y: 7, w: 10, h: 9, minH: 5, minW: 4 },
    { i: 'cash-position', x: 0, y: 16, w: 5, h: 8, minH: 5, minW: 3 },
    { i: 'ar-overview', x: 5, y: 16, w: 5, h: 8, minH: 5, minW: 4 },
    { i: 'ap', x: 0, y: 24, w: 5, h: 8, minH: 5, minW: 3 },
    { i: 'recent-sales', x: 5, y: 24, w: 5, h: 8, minH: 4, minW: 3 },
    { i: 'top-products', x: 0, y: 32, w: 5, h: 8, minH: 4, minW: 3 },
    { i: 'credits-today', x: 5, y: 32, w: 5, h: 8, minH: 5, minW: 3 },
    { i: 'ar-payments', x: 0, y: 40, w: 5, h: 7, minH: 5, minW: 3 },
    { i: 'pending-reviews', x: 5, y: 40, w: 5, h: 7, minH: 4, minW: 4 },
    { i: 'payables', x: 0, y: 47, w: 5, h: 7, minH: 4, minW: 3 },
    { i: 'audit-pulse', x: 5, y: 47, w: 5, h: 9, minH: 7, minW: 3 },
  ],
};

// ── MAIN DASHBOARD ───────────────────────────────────────────────────────────
export default function DashboardPage() {
  const { isConsolidatedView, switchBranch, selectedBranchId, currentBranch, user } = useAuth();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const [stats, setStats] = useState(null);
  const [branchSummary, setBranchSummary] = useState(null);
  const [poSummary, setPoSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState(null);
  const [detailType, setDetailType] = useState('sale');
  const openDetailModal = (num, type = 'sale') => { setSelectedInvoiceNumber(num); setDetailType(type); setInvoiceModalOpen(true); };
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtCustomer, setStmtCustomer] = useState(null);
  const openCustStmt = (id, name) => { if (!id) return; setStmtCustomer({ id, name }); setStmtOpen(true); };
  const [analyticsPeriod, setAnalyticsPeriod] = useState('this_month');
  const [unclosedDays, setUnclosedDays] = useState(null);
  // Self-heal banner: shown when settings.company_info is missing (e.g.
  // post-Reset). One-tap restore reads from the immutable organizations row
  // so SMS signatures stop reading just "- MAIN BRANCH".
  const [companyInfoMissing, setCompanyInfoMissing] = useState(false);
  const [companyInfoSuggested, setCompanyInfoSuggested] = useState(null);
  const [restoringCompanyInfo, setRestoringCompanyInfo] = useState(false);
  const { width: gridWidth, containerRef: gridRef, mounted: gridMounted } = useContainerWidth();

  const layoutKey = `${LAYOUT_KEY}_${user?.id || 'default'}_${isConsolidatedView ? 'owner' : 'branch'}`;
  const defaultLayout = isConsolidatedView ? OWNER_DEFAULT_LAYOUT : BRANCH_DEFAULT_LAYOUT;

  const [layouts, setLayouts] = useState(() => {
    try {
      const saved = localStorage.getItem(layoutKey);
      return saved ? validateLayouts(JSON.parse(saved), defaultLayout) : defaultLayout;
    } catch { return defaultLayout; }
  });

  // Synchronously reset layouts when the view switches (owner ↔ branch).
  // Uses the "getDerivedStateFromProps" hook pattern: calling setState during render
  // causes React to immediately re-render with updated state BEFORE painting to DOM,
  // preventing the grid from ever rendering with the wrong layout (which would trigger
  // onLayoutChange → write bad data to localStorage before a useEffect could correct it).
  const [prevLayoutKey, setPrevLayoutKey] = useState(layoutKey);
  if (prevLayoutKey !== layoutKey) {
    setPrevLayoutKey(layoutKey);
    try {
      const saved = localStorage.getItem(layoutKey);
      const loaded = saved ? validateLayouts(JSON.parse(saved), defaultLayout) : defaultLayout;
      setLayouts(loaded);
    } catch { setLayouts(defaultLayout); }
  }

  const onLayoutChange = (_, allLayouts) => {
    setLayouts(allLayouts);
    localStorage.setItem(layoutKey, JSON.stringify(allLayouts));
  };

  const resetLayout = () => {
    setLayouts(defaultLayout);
    localStorage.removeItem(layoutKey);
  };

  // Lock/Unlock the dashboard layout. Defaults to LOCKED so widgets can never
  // be dragged/resized by accident; the owner unlocks intentionally to arrange.
  // Persisted per-user across sessions.
  const lockKey = `${LAYOUT_KEY}_locked_${user?.id || 'default'}`;
  const [isLocked, setIsLocked] = useState(() => {
    const v = localStorage.getItem(lockKey);
    return v === null ? true : v === 'true';
  });
  const toggleLock = () => {
    setIsLocked((prev) => {
      const next = !prev;
      localStorage.setItem(lockKey, String(next));
      return next;
    });
  };

  const loadData = async (silent = false) => {
    // Skip network calls entirely when offline — Quick Launch still works,
    // and the offline placeholder UI is rendered instead of stale data.
    if (!isOnline) { setLoading(false); setRefreshing(false); return; }
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      if (isConsolidatedView) {
        const [summaryRes, statsRes, poRes] = await Promise.all([
          api.get('/dashboard/branch-summary'),
          api.get('/dashboard/stats'),
          api.get('/purchase-orders/unpaid-summary').catch(() => ({ data: null })),
        ]);
        setBranchSummary(summaryRes.data);
        setStats(statsRes.data);
        setPoSummary(poRes.data);
      } else {
        const [res, poRes] = await Promise.all([
          api.get('/dashboard/stats', { params: selectedBranchId !== 'all' ? { branch_id: selectedBranchId } : {} }),
          api.get('/purchase-orders/unpaid-summary').catch(() => ({ data: null })),
        ]);
        setStats(res.data);
        setPoSummary(poRes.data);
        setBranchSummary(null);
        // Fetch unclosed days for the selected branch
        if (selectedBranchId && selectedBranchId !== 'all') {
          try {
            const ucRes = await api.get('/daily-close/unclosed-days', { params: { branch_id: selectedBranchId } });
            const pastDays = (ucRes.data.unclosed_days || []).filter(d => d.date < localTodayStr());
            setUnclosedDays(pastDays.length > 0 ? { days: pastDays, last_close_date: ucRes.data.last_close_date } : null);
          } catch { setUnclosedDays(null); }
        } else {
          setUnclosedDays(null);
        }
      }
    } catch (err) { console.error('Dashboard load error', err); }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => { loadData(); }, [isConsolidatedView, selectedBranchId, isOnline]); // eslint-disable-line

  // Check if company_info is missing (post-reset self-heal)
  useEffect(() => {
    if (!isOnline) return; // skip when offline
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/settings/company-info-status');
        if (cancelled) return;
        setCompanyInfoMissing(!r.data?.has_company_info);
        setCompanyInfoSuggested(r.data?.suggested || null);
      } catch { /* silent — banner just won't show */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleRestoreCompanyInfo = async () => {
    setRestoringCompanyInfo(true);
    try {
      const r = await api.post('/settings/restore-company-info');
      if (r.data?.recreated) {
        toast.success(`Company recreated: ${r.data.value?.name || 'New Company'} — please reload the page.`);
        setCompanyInfoMissing(false);
        setTimeout(() => window.location.reload(), 1500);
      } else if (r.data?.restored) {
        toast.success(`Company info restored: ${r.data.value?.name}`);
        setCompanyInfoMissing(false);
      } else {
        toast.info('Company info already set');
        setCompanyInfoMissing(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore company info');
    }
    setRestoringCompanyInfo(false);
  };

  // ── Offline mode ────────────────────────────────────────────────────────
  // When offline, render a slim dashboard so managers can still use Quick
  // Launch. Stats / charts are replaced with a friendly placeholder card.
  if (!isOnline) {
    const today = localTodayStr();
    return (
      <div className="space-y-4 animate-fadeIn" data-testid="dashboard-offline">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 size={20} className="text-[#1A4D2E]" />
              <h1 className="text-[24px] font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--text-primary)' }}>Dashboard</h1>
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1 ml-1">
                <WifiOff size={11} /> Offline
              </Badge>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{today} · Limited data — sales & cached actions still work</p>
          </div>
        </div>

        {/* Quick Launch always works — no API calls */}
        <QuickLaunch />

        {/* Friendly offline placeholder where stats/widgets would be */}
        <Card className="border-dashed border-2 border-amber-200 bg-amber-50/50" data-testid="offline-placeholder-card">
          <CardContent className="py-10">
            <div className="flex flex-col items-center text-center max-w-md mx-auto">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-3">
                <CloudOff size={26} className="text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1.5" style={{ fontFamily: 'Manrope' }}>
                Live data needs an internet connection
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                Sales, AR, cash position, audit health, and charts will reappear automatically once you're back online. In the meantime, use <span className="font-semibold text-[#1A4D2E]">Quick Launch</span> above to head to Sales, Inventory or any cached area.
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <RefreshCw size={12} />
                <span>Auto-refreshes when connection is restored</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-slate-400">
      <RefreshCw size={20} className="animate-spin mr-2" /> Loading dashboard...
    </div>
  );

  const today = stats?.today || localTodayStr();
  const dayOfWeek = stats?.day_of_week || '';

  // ─── OWNER CONSOLIDATED VIEW ─────────────────────────────────────────────
  if (isConsolidatedView && branchSummary) {
    const totals = branchSummary.totals || {};
    const branches = branchSummary.branches || [];

    const ownerWidgets = {
      'kpis': (
        <div className="p-4 h-full">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 h-full auto-rows-fr">
            {[
              { label: 'Total Sales Today', value: formatPHP(totals.today_revenue || 0), icon: DollarSign, color: { bg: 'bg-emerald-50', text: 'text-emerald-700' }, sub: `${branches.reduce((s, b) => s + (b.today_sales_count || 0), 0)} transactions` },
              { label: 'Cash Position', value: formatPHP(totals.total_cash || 0), icon: Wallet, color: { bg: 'bg-blue-50', text: 'text-blue-700' }, sub: 'Cashier + Safe' },
              { label: 'Outstanding AR', value: formatPHP(totals.total_receivables || 0), icon: Receipt, color: { bg: 'bg-amber-50', text: 'text-amber-700' }, sub: 'Unpaid credit' },
              { label: 'Low Stock', value: totals.low_stock_count || 0, icon: AlertTriangle, color: { bg: 'bg-red-50', text: (totals.low_stock_count || 0) > 0 ? 'text-red-600' : 'text-slate-400' }, sub: 'Below threshold' },
              { label: 'Audit Health', value: stats?.last_audit ? `${stats.last_audit.overall_score}/100` : 'N/A', icon: ShieldCheck, color: { bg: 'bg-violet-50', text: stats?.last_audit?.overall_score >= 80 ? 'text-emerald-600' : 'text-amber-600' }, sub: stats?.days_since_audit != null ? `${stats.days_since_audit}d ago` : 'No audit' },
            ].map(kpi => <KpiCard key={kpi.label} {...kpi} testId={kpi.testId || ('dashboard-kpi-' + kpi.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))} />)}
          </div>
        </div>
      ),
      'sales-trends': <SalesTrendsWidget branchId={null} onPeriodChange={setAnalyticsPeriod} />,
      'ap': <AccountsPayableWidget branchId={null} />,
      'branch-comparison': <BranchComparisonWidget period={analyticsPeriod} branchId={null} />,
      'pending-reviews': (
        <Card className="border-slate-200 h-full">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><FileCheck size={14} className="text-amber-600" /> Pending Reviews</CardTitle></CardHeader>
          <CardContent><PendingReviewsWidget /></CardContent>
        </Card>
      ),
      'branches': (
        <div className="p-4">
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}><BarChart3 size={16} className="text-[#1A4D2E]" /> Branch Performance Today</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map(branch => <BranchCard key={branch.id} branch={branch} onSelect={switchBranch} />)}
          </div>
        </div>
      ),
      'payables': (
        <Card className="border-slate-200 h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><CalendarClock size={14} className="text-red-600" /> Upcoming Payables</CardTitle>
          </CardHeader>
          <CardContent>
            {poSummary?.total_count > 0 ? (
              <div className="space-y-1.5 text-xs">
                {[...(poSummary.overdue || []), ...(poSummary.due_soon || [])].slice(0, 5).map(po => (
                  <div key={po.id || po.po_number} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
                    <div><p className="font-semibold">{po.vendor}</p><button className="text-blue-600 hover:underline" onClick={() => openDetailModal(po.po_number, 'po')}>{po.po_number}</button></div>
                    <p className="font-bold text-red-700">{formatPHP(po.balance)}</p>
                  </div>
                ))}
                <button onClick={() => navigate('/pay-supplier')} className="text-[10px] text-[#1A4D2E] hover:underline">View all →</button>
              </div>
            ) : <p className="text-xs text-emerald-600">All payments up to date</p>}
          </CardContent>
        </Card>
      ),
      'alerts': (
        <Card className="border-slate-200 h-full">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><AlertTriangle size={14} className="text-amber-500" /> Alerts</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {branches.filter(b => b.low_stock_count > 0).map(b => (
              <div key={b.id} className="flex items-center justify-between p-2 rounded bg-amber-50 text-xs">
                <span className="font-medium">{b.name}</span><Badge className="bg-amber-100 text-amber-700 text-[10px]">{b.low_stock_count} low stock</Badge>
              </div>
            ))}
            {!branches.some(b => b.low_stock_count > 0) && <p className="text-xs text-slate-400">No alerts</p>}
          </CardContent>
        </Card>
      ),
      'top-revenue': (
        <Card className="border-slate-200 h-full">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><TrendingUp size={14} className="text-emerald-600" /> Top Revenue Today</CardTitle></CardHeader>
          <CardContent>
            {[...branches].sort((a, b) => (b.today_revenue || 0) - (a.today_revenue || 0)).slice(0, 8).map((b, i) => (
              <div key={b.id} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                  <span className="text-xs">{b.name}</span>
                </div>
                <span className="font-semibold text-emerald-700 text-xs">{formatPHP(b.today_revenue || 0)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      ),
      'internal-profit': <InternalProfitWidget />,
      'audit-pulse': <AuditPulseWidget branchId={null} />,
    };

    return (
      <div className="space-y-4 animate-fadeIn" data-testid="owner-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Building2 size={20} className="text-[#1A4D2E]" />
              <h1 className="text-[24px] font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--text-primary)' }}>Owner Dashboard</h1>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{dayOfWeek}, {today} · {branches.length} branches</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm" onClick={toggleLock}
              data-testid="dashboard-lock-toggle"
              title={isLocked ? 'Unlock to rearrange widgets' : 'Lock layout'}
              className={isLocked ? '' : 'bg-[#C85A32] text-white border-transparent hover:bg-[#b04f2c] hover:text-white'}
            >
              {isLocked
                ? <><Lock size={13} className="mr-1" /> Locked</>
                : <><Unlock size={13} className="mr-1" /> Unlocked</>}
            </Button>
            {!isLocked && (
              <Button variant="outline" size="sm" onClick={resetLayout} title="Reset layout" data-testid="dashboard-reset-layout-button">
                <RotateCcw size={13} className="mr-1" /> Reset Layout
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => loadData(true)} disabled={refreshing}>
              <RefreshCw size={13} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Quick Launch — collapsible row of the 7 most-used manager actions */}
        <QuickLaunch />

        <div
          ref={gridRef}
          className={`rounded-xl transition-all ${!isLocked ? 'bg-[radial-gradient(#d6d3d1_1px,transparent_1px)] [background-size:16px_16px] ring-1 ring-stone-200 -m-2 p-2' : ''}`}
        >
        {gridMounted && (
        <Responsive
          width={gridWidth}
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 768 }}
          cols={{ lg: 12, md: 10 }}
          rowHeight={30}
          onLayoutChange={onLayoutChange}
          draggableHandle=".drag-handle"
          isDraggable={!isLocked}
          isResizable={!isLocked}
          resizeHandles={['se']}
          compactType="vertical"
          containerPadding={[0, 0]}
          margin={[16, 16]}
        >
          {Object.entries(ownerWidgets).map(([key, widget]) => (
            <div key={key} className="h-full">
              <GridWidget locked={isLocked} title={key === 'kpis' || key === 'branches' ? null : key.replace(/-/g, ' ')}>
                {widget}
              </GridWidget>
            </div>
          ))}
        </Responsive>
        )}
        </div>
      </div>
    );
  }

  // ─── BRANCH / SINGLE VIEW ────────────────────────────────────────────────
  const ar = stats?.ar_aging || {};
  const todayNetCash = stats?.today_net_cash || 0;
  const lastClose = stats?.last_close_date;
  const daysSinceClose = stats?.days_since_close;

  const branchWidgets = {
    'kpis': (
      <div className="p-4 h-full">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 h-full auto-rows-fr">
          {[
            { label: 'Total Sales', value: formatPHP(stats?.today_revenue), icon: DollarSign, color: { bg: 'bg-emerald-50', text: 'text-emerald-700' }, sub: `${stats?.today_sales_count || 0} txns` },
            { label: 'Cash Sales', value: formatPHP(stats?.today_cash_sales), icon: Banknote, color: { bg: 'bg-green-50', text: 'text-green-700' }, sub: stats?.today_digital_sales > 0 ? `+${formatPHP(stats?.today_digital_sales)} digital` : 'Cash received' },
            { label: 'New Credit', value: formatPHP(stats?.today_credit_sales), icon: CreditCard, color: { bg: 'bg-amber-50', text: 'text-amber-700' }, sub: 'AR created today' },
            { label: 'AR Collected', value: formatPHP(stats?.today_ar_collected), icon: Receipt, color: { bg: 'bg-blue-50', text: 'text-blue-700' }, sub: 'Payments received' },
            { label: 'Expenses', value: formatPHP(stats?.today_expenses), icon: ArrowDown, color: { bg: 'bg-red-50', text: 'text-red-600' }, sub: 'Paid out today' },
            ...(stats?.today_discounts?.total_discount > 0 ? [{
              label: 'Discounts', value: formatPHP(stats.today_discounts.total_discount), icon: Percent,
              color: { bg: 'bg-rose-50', text: 'text-rose-600' },
              sub: `${stats.today_discounts.transaction_count || 0} txn${(stats.today_discounts.transaction_count || 0) !== 1 ? 's' : ''}`
            }] : []),
            ...(stats?.bir_active ? [{
              label: 'BIR Today', value: stats.bir_today_count || 0, icon: FileCheck,
              color: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
              sub: 'Official receipts', link: '/bir-compliance', testId: 'kpi-bir-today',
            }] : []),
            { label: 'Net Cash Flow', value: (todayNetCash >= 0 ? '+' : '') + formatPHP(todayNetCash), icon: todayNetCash >= 0 ? TrendingUp : ArrowDownRight, color: { bg: todayNetCash >= 0 ? 'bg-emerald-50' : 'bg-red-50', text: todayNetCash >= 0 ? 'text-emerald-700' : 'text-red-600' }, sub: 'Cash + digital − out' },
          ].map(kpi => <KpiCard key={kpi.label} {...kpi} testId={kpi.testId || ('dashboard-kpi-' + kpi.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))} />)}
        </div>
      </div>
    ),
    'sales-trends': <SalesTrendsWidget branchId={selectedBranchId} onPeriodChange={setAnalyticsPeriod} />,
    'cash-position': (
      <Card className="border-slate-200 h-full" style={{ border: '1px solid var(--border-default)' }}>
        <CardHeader className="pb-2"><CardTitle className="text-[13px] font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}><Wallet size={14} className="text-blue-600" /> Cash Position</CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {[
            { label: 'Operating Fund', value: stats?.cashier_balance || 0, tone: 'info'    },
            { label: 'Safe',           value: stats?.safe_balance    || 0, tone: 'neutral'  },
            { label: 'Total Cash',     value: stats?.total_cash_position || 0, tone: 'success', bold: true },
          ].map(({ label, value, tone, bold }) => {
            const toneMap = { info: '#2563EB', neutral: '#475569', success: '#15803D' };
            return (
              <div key={label} className="flex justify-between items-center px-3 py-2.5 rounded-md" style={{ backgroundColor: 'var(--surface-canvas)' }}>
                <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
                <span
                  className="text-[14px]"
                  style={{
                    fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"',
                    fontWeight: bold ? 700 : 600,
                    color: toneMap[tone] || 'var(--text-primary)',
                  }}
                >
                  {formatPHP(value)}
                </span>
              </div>
            );
          })}
          {stats?.inventory_value && (<>
            <div className="border-t pt-2 mt-1" style={{ borderColor: 'var(--border-subtle)' }}>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded-md" style={{ backgroundColor: 'var(--surface-canvas)' }}>
                  <p className="text-[10px] mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Stock @ Capital</p>
                  <p className="text-[13px] font-semibold" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{formatPHP(stats.inventory_value.capital_value)}</p>
                </div>
                <div className="p-2 rounded-md" style={{ backgroundColor: 'var(--success-tint)' }}>
                  <p className="text-[10px] mb-0.5" style={{ color: 'var(--success-icon)' }}>Stock @ Retail</p>
                  <p className="text-[13px] font-semibold" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--num-positive)' }}>{formatPHP(stats.inventory_value.retail_value)}</p>
                </div>
              </div>
            </div>
          </>)}
        </CardContent>
      </Card>
    ),
    'ar-overview': (
      <Card className="h-full" style={{ border: '1px solid var(--border-default)' }}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-[13px] font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}><Receipt size={14} className="text-amber-600" /> Accounts Receivable</CardTitle>
            <div className="text-right">
              <p className="text-[20px] font-semibold text-amber-700" style={{ fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"', fontFamily: 'Manrope, sans-serif' }}>{formatPHP(ar.total || 0)}</p>
              <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Outstanding</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <AgingBar aging={ar} />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-tertiary)' }}>Top Debtors</p>
              {(stats?.top_debtors || []).length === 0 ? <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>No outstanding AR</p>
                : (stats?.top_debtors || []).map((d, i) => (
                  <div key={i} className="flex justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                    <span className="font-medium truncate max-w-[110px]">{d.customer}</span>
                    <span className="font-semibold text-amber-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(d.balance)}</span>
                  </div>
                ))}
            </div>
          </div>
        </CardContent>
      </Card>
    ),
    'ap': <AccountsPayableWidget branchId={selectedBranchId} />,
    'audit-pulse': <AuditPulseWidget branchId={selectedBranchId !== 'all' ? selectedBranchId : null} />,
    'pending-reviews': (
      <Card className="border-slate-200 h-full">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><FileCheck size={14} className="text-amber-600" /> Receipts Awaiting Review</CardTitle></CardHeader>
        <CardContent><PendingReviewsWidget branchId={selectedBranchId !== 'all' ? selectedBranchId : undefined} compact={true} /></CardContent>
      </Card>
    ),
    'credits-today': (
      <Card className="border-slate-200 h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><CreditCard size={15} className="text-amber-600" /> Credit Extended Today</CardTitle>
            <span className="text-xs font-semibold text-amber-700">{formatPHP(stats?.today_credit_sales || 0)}</span>
          </div>
        </CardHeader>
        <CardContent>
          {(stats?.credit_customers_today || []).length === 0 ? <p className="text-xs text-slate-400">No new credit today</p>
            : <div className="space-y-1.5">{(stats?.credit_customers_today || []).map((c, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-amber-50 rounded px-3 py-2">
                <div><p className="font-semibold">{c.customer_id ? <button onClick={() => openCustStmt(c.customer_id, c.customer_name)} className="hover:text-[#1A4D2E] hover:underline">{c.customer_name}</button> : c.customer_name}</p><button className="text-blue-600 hover:underline" onClick={() => openDetailModal(c.invoice_number)}>{c.invoice_number}</button></div>
                <div className="text-right"><p className="font-bold text-amber-700">{formatPHP(c.amount)}</p><p className="text-slate-400">bal {formatPHP(c.balance)}</p></div>
              </div>
            ))}</div>
          }
        </CardContent>
      </Card>
    ),
    'ar-payments': (
      <Card className="border-slate-200 h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Banknote size={15} className="text-blue-600" /> AR Payments Today</CardTitle>
            <span className="text-xs font-semibold text-blue-700">{formatPHP(stats?.today_ar_collected || 0)}</span>
          </div>
        </CardHeader>
        <CardContent>
          {(stats?.recent_ar_payments || []).length === 0 ? <p className="text-xs text-slate-400">No AR payments today</p>
            : <div className="space-y-1.5">{(stats?.recent_ar_payments || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-blue-50 rounded px-3 py-2">
                <div><p className="font-semibold">{p.customer_id ? <button onClick={() => openCustStmt(p.customer_id, p.customer_name)} className="hover:text-[#1A4D2E] hover:underline">{p.customer_name}</button> : p.customer_name}</p><button className="text-blue-600 hover:underline" onClick={() => openDetailModal(p.invoice_number)}>{p.invoice_number}</button></div>
                <p className="font-bold text-blue-700">{formatPHP(p.amount)}</p>
              </div>
            ))}</div>
          }
        </CardContent>
      </Card>
    ),
    'payables': (
      <Card className="border-slate-200 h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><CalendarClock size={14} className="text-red-600" /> Upcoming Payables</CardTitle>
        </CardHeader>
        <CardContent>
          {poSummary?.total_count > 0 ? (
            <div className="space-y-1.5 text-xs">
              {[...(poSummary.overdue || []), ...(poSummary.due_soon || [])].slice(0, 5).map(po => (
                <div key={po.id || po.po_number} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <div><p className="font-semibold">{po.vendor}</p><button className="text-blue-600 hover:underline" onClick={() => openDetailModal(po.po_number, 'po')}>{po.po_number}</button></div>
                  <p className="font-bold text-red-700">{formatPHP(po.balance)}</p>
                </div>
              ))}
              <button onClick={() => navigate('/pay-supplier')} className="text-[10px] text-[#1A4D2E] hover:underline">View all →</button>
            </div>
          ) : <p className="text-xs text-emerald-600">All payments up to date</p>}
        </CardContent>
      </Card>
    ),
    'top-products': (
      <Card className="border-slate-200 h-full">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><TrendingUp size={14} className="text-emerald-600" /> Top Products</CardTitle></CardHeader>
        <CardContent>
          {(stats?.top_products || []).length === 0 ? <p className="text-xs text-slate-400">No sales data</p>
            : (stats?.top_products || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center text-[9px] font-bold text-slate-500">{i + 1}</span>
                  <span className="truncate max-w-[100px]">{p.name}</span>
                </div>
                <div className="text-right"><p className="font-semibold text-emerald-700">{formatPHP(p.revenue)}</p><p className="text-slate-400">{p.quantity} sold</p></div>
              </div>
            ))}
        </CardContent>
      </Card>
    ),
    'recent-sales': (
      <Card className="border-slate-200 h-full">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Recent Transactions</CardTitle></CardHeader>
        <CardContent>
          {(stats?.recent_sales || []).length === 0 ? <p className="text-xs text-slate-400">No recent sales</p>
            : (stats?.recent_sales || []).slice(0, 5).map(sale => (
              <div key={sale.id} className="py-1.5 border-b border-slate-50 last:border-0">
                <div className="flex justify-between items-start text-xs">
                  <div><p className="font-semibold"><button className="text-blue-600 hover:underline" onClick={() => openDetailModal(sale.invoice_number || sale.sale_number)}>{sale.invoice_number || sale.sale_number}</button></p><p className="text-slate-500">{sale.customer_id ? <button onClick={() => openCustStmt(sale.customer_id, sale.customer_name)} className="hover:text-[#1A4D2E] hover:underline">{sale.customer_name || 'Walk-in'}</button> : (sale.customer_name || 'Walk-in')}</p></div>
                  <div className="text-right">
                    <p className="font-bold">{formatPHP(sale.grand_total || sale.total)}</p>
                    <Badge className={`text-[9px] ${sale.payment_type === 'cash' ? 'bg-emerald-100 text-emerald-700' : sale.payment_type === 'digital' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                      {sale.payment_type || 'cash'}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
        </CardContent>
      </Card>
    ),
  };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight leading-tight" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}>Dashboard</h1>
          <p className="text-[13px] mt-0.5 flex items-center gap-2" style={{ color: 'var(--text-tertiary)' }}>
            <Calendar size={13} /> {dayOfWeek}, {today}
            {lastClose && <span className={`flex items-center gap-1 ${daysSinceClose > 0 ? 'text-amber-600' : 'text-emerald-600'}`}><Lock size={11} /> {daysSinceClose === 0 ? 'Closed today' : `Last close: ${lastClose}`}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={toggleLock}
            data-testid="dashboard-lock-toggle"
            title={isLocked ? 'Unlock to rearrange widgets' : 'Lock layout'}
            className={isLocked ? '' : 'bg-[#C85A32] text-white border-transparent hover:bg-[#b04f2c] hover:text-white'}
          >
            {isLocked
              ? <><Lock size={13} className="mr-1" /> Locked</>
              : <><Unlock size={13} className="mr-1" /> Unlocked</>}
          </Button>
          {!isLocked && (
            <Button variant="outline" size="sm" onClick={resetLayout} title="Reset layout" data-testid="dashboard-reset-layout-button"><RotateCcw size={13} className="mr-1" /> Reset Layout</Button>
          )}
          <Button variant="outline" size="sm" onClick={() => loadData(true)} disabled={refreshing}><RefreshCw size={13} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh</Button>
          <Button size="sm" onClick={() => navigate('/close-wizard')} className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-9"><Lock size={13} className="mr-1.5" /> Close Wizard</Button>
        </div>
      </div>

      {/* Quick Launch — collapsible row of the 7 most-used manager actions */}
      <QuickLaunch />

      {/* Self-heal banner: company_info missing (typically post-Reset) */}
      {companyInfoMissing && (
        <div className="flex items-center justify-between px-4 py-3 rounded-lg border border-emerald-300 bg-emerald-50" data-testid="dashboard-restore-company-info">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Building2 size={18} className="text-emerald-700" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-900">
                Your company info is missing
              </p>
              <p className="text-xs text-emerald-700">
                {companyInfoSuggested?.name
                  ? <>Restore <b>{companyInfoSuggested.name}</b> as your business name so SMS signatures &amp; receipts read correctly.</>
                  : 'Restore your business name so SMS signatures and receipts read correctly.'}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleRestoreCompanyInfo}
            disabled={restoringCompanyInfo}
            className="bg-emerald-700 hover:bg-emerald-800 text-white flex-shrink-0"
            data-testid="dashboard-restore-company-info-btn">
            {restoringCompanyInfo ? <RefreshCw size={13} className="mr-1.5 animate-spin" /> : <Building2 size={13} className="mr-1.5" />}
            {restoringCompanyInfo ? 'Restoring...' : 'Restore Company Info'}
          </Button>
        </div>
      )}

      {/* Unclosed Days Warning */}
      {unclosedDays && unclosedDays.days.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 rounded-lg border border-amber-300 bg-amber-50" data-testid="dashboard-unclosed-warning">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={18} className="text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800">
                {unclosedDays.days.length} unclosed day{unclosedDays.days.length > 1 ? 's' : ''} need attention
              </p>
              <p className="text-xs text-amber-600">
                {unclosedDays.days[0].date} to {unclosedDays.days[unclosedDays.days.length - 1].date}
                {unclosedDays.last_close_date ? ` · Last closed: ${unclosedDays.last_close_date}` : ' · No previous close on record'}
              </p>
            </div>
          </div>
          <Button size="sm" onClick={() => navigate('/close-wizard')} className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0" data-testid="dashboard-go-close-wizard">
            <Lock size={13} className="mr-1.5" /> Close Wizard
          </Button>
        </div>
      )}

      <div
        ref={gridRef}
        className={`rounded-xl transition-all ${!isLocked ? 'bg-[radial-gradient(#d6d3d1_1px,transparent_1px)] [background-size:16px_16px] ring-1 ring-stone-200 -m-2 p-2' : ''}`}
      >
      {gridMounted && (
      <Responsive
        width={gridWidth}
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 768 }}
        cols={{ lg: 12, md: 10 }}
        rowHeight={30}
        onLayoutChange={onLayoutChange}
        draggableHandle=".drag-handle"
        isDraggable={!isLocked}
        isResizable={!isLocked}
        resizeHandles={['se']}
        compactType="vertical"
        containerPadding={[0, 0]}
        margin={[16, 16]}
      >
        {Object.entries(branchWidgets).map(([key, widget]) => (
          <div key={key} className="h-full">
            <GridWidget locked={isLocked} title={key === 'kpis' ? null : key.replace(/-/g, ' ')}>
              {widget}
            </GridWidget>
          </div>
        ))}
      </Responsive>
      )}
      </div>
      <ReviewDetailDialog
        open={invoiceModalOpen && detailType === 'po'}
        onOpenChange={(open) => { if (!open) setInvoiceModalOpen(false); }}
        poNumber={selectedInvoiceNumber}
        showReviewAction={false}
        showPayAction={false}
      />
      <InvoiceDetailModal compact
        open={invoiceModalOpen && detailType === 'sale'}
        onOpenChange={(open) => { if (!open) setInvoiceModalOpen(false); }}
        invoiceNumber={selectedInvoiceNumber}
      />
      <CustomerStatementModal
        open={stmtOpen}
        onOpenChange={setStmtOpen}
        customer={stmtCustomer}
      />
    </div>
  );
}
